import { useEffect, useState } from "react";
import { useLive } from "../../state/use-live.js";
import { storage } from "../../state/storage.js";
import { AssetImage } from "./AssetImage.js";
import { useAssetUrl } from "../../state/use-asset-url.js";
import { generateImage } from "../../state/image-actions.js";
import { useT } from "../../i18n/use-t.js";
import type { GeneratedImage } from "@roqeet/core";

const GALLERY_RENDER_WINDOW = 120;

/**
 * 생성 이미지 갤러리(결정 #19). provenance 레코드를 반응형으로 읽어(결정 #13)
 * 콘텐츠 주소화 blob에서 lazy objectURL로 표시한다(결정 #18). 썸네일은
 * AssetImage가 화면에 있을 때만 URL을 만들고 떠나면 revoke한다.
 *
 * 헤더에서 프롬프트로 이미지를 생성하면(결정 #19) provenance가 기록되어 즉시
 * 그리드에 반응형으로 추가된다. 클릭하면 라이트박스로 원본 + 파라미터를 본다.
 */
export function GalleryView() {
  const { t } = useT();
  const images = useLive<GeneratedImage[]>(() => storage.liveGeneratedImages(), []);
  const [selected, setSelected] = useState<GeneratedImage | null>(null);
  const [prompt, setPrompt] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [visibleCount, setVisibleCount] = useState(GALLERY_RENDER_WINDOW);
  const visibleImages = (images ?? []).slice(0, visibleCount);
  const hiddenCount = Math.max(0, (images?.length ?? 0) - visibleImages.length);

  useEffect(() => {
    setVisibleCount(GALLERY_RENDER_WINDOW);
  }, [images?.length]);

  const handleGenerate = async () => {
    const p = prompt.trim();
    if (!p || busy) return;
    setBusy(true);
    setError(null);
    try {
      await generateImage({ prompt: p });
      setPrompt("");
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex h-full flex-col">
      <div className="flex flex-col gap-2 border-b border-line p-3">
        <div className="flex items-center gap-2">
          <span className="text-sm text-ink-soft">
            {images ? t("gallery.count", { count: images.length }) : t("common.loading")}
          </span>
        </div>
        <div className="flex items-end gap-2">
          <textarea
            className="min-h-[40px] max-h-32 flex-1 resize-none rounded-xl border border-line bg-bg-soft px-3 py-2 text-ink outline-none focus:border-accent"
            placeholder={t("gallery.promptPlaceholder")}
            rows={1}
            value={prompt}
            disabled={busy}
            onChange={(e) => setPrompt(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                void handleGenerate();
              }
            }}
          />
          <button
            className="rounded-xl bg-accent px-4 py-2 text-sm font-medium text-white hover:bg-accent-soft disabled:opacity-50"
            onClick={handleGenerate}
            disabled={busy || !prompt.trim()}
          >
            {busy ? t("gallery.generating") : t("gallery.generate")}
          </button>
        </div>
        {error && <p className="text-xs text-red-300">{error}</p>}
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto p-3 scroll-touch">
        {images?.length === 0 ? (
          <p className="px-1 py-8 text-center text-sm text-ink-faint">
            {t("gallery.empty")}
          </p>
        ) : (
          <>
          <ul className="grid grid-cols-2 gap-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5">
            {visibleImages.map((img) => (
              <li key={img.id}>
                <button
                  className="block aspect-square w-full overflow-hidden rounded-lg border border-line bg-bg-raised"
                  title={img.prompt ?? img.provider}
                  onClick={() => setSelected(img)}
                >
                  <AssetImage
                    assetId={img.assetId}
                    alt={img.prompt ?? t("gallery.imageAlt")}
                    className="h-full w-full object-cover"
                  />
                </button>
              </li>
            ))}
          </ul>
          {hiddenCount > 0 && (
            <div className="mt-3 flex justify-center">
              <button
                className="rounded-lg border border-line px-3 py-1.5 text-xs text-ink-soft hover:bg-bg-raised"
                onClick={() => setVisibleCount((count) => count + GALLERY_RENDER_WINDOW)}
              >
                {t("chat.loadMore", { count: Math.min(GALLERY_RENDER_WINDOW, hiddenCount), hidden: hiddenCount })}
              </button>
            </div>
          )}
          </>
        )}
      </div>

      {selected && <Lightbox image={selected} onClose={() => setSelected(null)} />}
    </div>
  );
}

/** 원본 이미지 + 생성 파라미터를 보여주는 라이트박스. */
function Lightbox({ image, onClose }: { image: GeneratedImage; onClose: () => void }) {
  const { t } = useT();
  const url = useAssetUrl(image.assetId);

  return (
    <div
      className="fixed inset-0 z-40 flex flex-col bg-black/80 p-4"
      role="dialog"
      aria-modal="true"
      onClick={onClose}
    >
      <div className="flex justify-end">
        <button
          className="rounded-lg bg-bg-raised px-3 py-1.5 text-sm text-ink"
          onClick={onClose}
          aria-label={t("gallery.close")}
        >
          {t("gallery.close")}
        </button>
      </div>
      <div className="flex min-h-0 flex-1 items-center justify-center" onClick={(e) => e.stopPropagation()}>
        {url ? (
          <img src={url} alt={image.prompt ?? t("gallery.imageAlt")} className="max-h-full max-w-full rounded-lg object-contain" />
        ) : (
          <span className="text-sm text-ink-faint">{t("common.loading")}</span>
        )}
      </div>
      <div
        className="mx-auto mt-3 w-full max-w-2xl space-y-1 rounded-xl bg-bg-soft/90 p-3 text-xs text-ink-soft"
        onClick={(e) => e.stopPropagation()}
      >
        {image.prompt && <Param label={t("gallery.paramPrompt")} value={image.prompt} />}
        {image.negativePrompt && <Param label={t("gallery.paramNegative")} value={image.negativePrompt} />}
        <div className="flex flex-wrap gap-x-4 gap-y-1">
          <Param label={t("gallery.paramProvider")} value={image.provider} inline />
          {image.model && <Param label={t("gallery.paramModel")} value={image.model} inline />}
          {image.seed != null && <Param label={t("gallery.paramSeed")} value={String(image.seed)} inline />}
          {image.steps != null && <Param label={t("gallery.paramSteps")} value={String(image.steps)} inline />}
        </div>
      </div>
    </div>
  );
}

function Param({ label, value, inline }: { label: string; value: string; inline?: boolean }) {
  if (inline) {
    return (
      <span>
        <span className="text-ink-faint">{label}:</span> {value}
      </span>
    );
  }
  return (
    <div>
      <span className="text-ink-faint">{label}: </span>
      <span className="break-words">{value}</span>
    </div>
  );
}
